import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../utils/classNames';
import { trackInitiateCheckout, navigateAfterTracking, ProductKey } from './MetaPixel';
import { prefetchPage } from '../utils/prefetch';
export const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 16
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.3,
  ease: [0.23, 1, 0.32, 1],
  delay
}} className={className} data-id="element-378" data-mp="18706ebgwgmef">
    {children}
  </motion.div>;
interface ButtonProps {
  href: string;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary';
  fullWidth?: boolean;
  className?: string;
  /** Fires Meta Pixel `InitiateCheckout` before navigating. */
  trackingProduct?: ProductKey;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}
export const CTAButton = ({
  href,
  children,
  variant = 'primary',
  fullWidth = false,
  className = '',
  trackingProduct,
  onClick
}: ButtonProps) => {
  const isExternal = href.startsWith('http');
  // Warm the checkout page as soon as the user shows intent (hover / touch / focus)
  const warmUp = isExternal ? () => prefetchPage(href) : undefined;
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (onClick) onClick(e);
    // Drop focus so the button never stays "lit" when coming back.
    e.currentTarget.blur();
    if (!trackingProduct) return;
    try {
      trackInitiateCheckout(trackingProduct);
    } catch {
      // never block navigation on tracking failure
    }
    const opensElsewhere = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
    if (isExternal && !opensElsewhere) {
      e.preventDefault();
      navigateAfterTracking(href);
    }
  };
  return <a href={href} onClick={handleClick} onPointerEnter={warmUp} onTouchStart={warmUp} onFocus={warmUp} rel={isExternal ? 'noopener' : undefined} className={cn('group/cta inline-flex items-center justify-center gap-2 rounded-full text-center transition-[transform,background-color,border-color,color,box-shadow] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 active:scale-[0.98]', fullWidth && 'w-full', variant === 'primary' ? 'bg-blue-600 text-white font-bold uppercase tracking-wide text-base px-8 py-4 shadow-[0_10px_30px_-10px_rgba(37,99,235,0.8)] [@media(hover:hover)]:hover:bg-blue-500 [@media(hover:hover)]:hover:-translate-y-0.5 animate-glow-button' : 'bg-white/5 text-white font-semibold border border-white/15 text-base px-7 py-3.5 [@media(hover:hover)]:hover:bg-white/10', className)} data-id="element-379" data-mp="22vkoonqpaqqy">
      {children}
    </a>;
};
export const Section = ({
  children,
  className = '',
  id
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => <section id={id} className={cn('px-5 md:px-8 py-16 md:py-24 overflow-hidden', className)} data-id="element-380" data-mp="o6z0jtvmnu2c">
    <div className="max-w-6xl mx-auto" data-id="element-381" data-mp="j84fela6ttf2">{children}</div>
  </section>;
export const Accent = ({
  children
}: {
  children: React.ReactNode;
}) => <span className="text-sky-400" data-id="element-382" data-mp="szie4qubep75">{children}</span>;
export const SectionTitle = ({
  children,
  sub,
  className = ''
}: {
  children: React.ReactNode;
  sub?: React.ReactNode;
  className?: string;
}) => <div className={cn('text-center mb-10 md:mb-14', className)} data-id="element-383" data-mp="c4lh4xngfsu">
    <h2 className="font-display font-black italic uppercase tracking-tight text-white text-[34px] leading-[0.95] md:text-6xl" data-id="element-384" data-mp="eam6guw67e72">
      {children}
    </h2>
    {sub && <p className="mt-4 text-slate-400 text-base md:text-lg max-w-2xl mx-auto" data-id="element-385" data-mp="abdbxntb3cag">
        {sub}
      </p>}
  </div>;
export const pad2 = (n: number) => n.toString().padStart(2, '0');